import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ResponseColumnHeader } from '../services/api/interaction/types';
import { DataTableColumnReorderEvent } from 'primevue';
import { createProtocolTableColumns } from '../components/coaching/protocol/table/protocolTableUtils';

const sortColumns = (a: ResponseColumnHeader, b: ResponseColumnHeader) => {
    if (a.order == null && b.order == null) return 0;
    if (a.order == null) return 1;
    if (b.order == null) return -1;
    return a.order - b.order;
};

export const interactionsTableStore = () => {
    const expandedRows = ref<Record<string, boolean>>({});
    const openedRows = ref<Record<string, boolean>>({});
    const rowsIdList = ref<string[]>([]);
    const showSummary = ref<boolean>(false);
    const expandedInSummaryMode = ref<Record<string, boolean>>({});
    const allRowsExpanded = computed(
        () => Object.keys(expandedRows.value).length === rowsIdList.value.length
    );
    const allRowsExpansionActive = ref<boolean>(false);

    const selectedRows = ref<Record<string, boolean>>({});
    // Never true today: the "select multiple" checkbox that called toggleSelectionMode was removed in the redesign,
    // so the row checkboxes and the approve-all and delete-selected buttons can't be reached.
    const selectionMode = ref<boolean>(false);
    const currentSortFieldId = ref<string>('');
    const currentSortOrder = ref<number | undefined>(undefined);

    const tableColumns = ref<ResponseColumnHeader[] | null>(null);

    const visibleColumns = computed(() =>
        (tableColumns.value ?? [])
            .filter((c) => c.order != null)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );

    const expandedColumns = ref<
        Record<
            string,
            { open: boolean; children: ResponseColumnHeader[]; column?: ResponseColumnHeader }
        >
    >({});

    const showCoachResultAsPoints = ref<boolean>(false);
    const showOnlyMyEvents = ref<boolean>(false);
    const showHeatMap = ref<boolean>(false);

    const toggleColumnExpansion = (columnId: string) => {
        if (expandedColumns.value[columnId]) {
            const shouldClose = expandedColumns.value[columnId].open === true;
            expandedColumns.value[columnId].open = !expandedColumns.value[columnId].open;
            if (shouldClose) {
                expandedColumns.value[columnId].children.forEach((childCol) => {
                    if (expandedColumns.value[childCol.id]) {
                        expandedColumns.value[childCol.id].open = false;
                    }
                });
            }
        }
    };

    const setExpandableColumns = (newColumns: ResponseColumnHeader[]) => {
        const { columns, expandedColumns: expandedCols } = createProtocolTableColumns(
            newColumns.map((col) => ({ ...col }))
        );
        tableColumns.value = columns;
        expandedColumns.value = expandedCols;
    };

    const setTableColumns = (newColumns: ResponseColumnHeader[]) =>
        (tableColumns.value = newColumns.map((col) => ({ ...col })).sort(sortColumns));

    // Keeps each known column's current order instead of the server's, so a refetch can't
    // race and overwrite a reorder/hide save still in flight.
    const reconcileTableColumns = (newColumns: ResponseColumnHeader[]) => {
        const existingById = new Map((tableColumns.value ?? []).map((col) => [col.id, col]));

        tableColumns.value = newColumns
            .map((col) => {
                const existing = existingById.get(col.id);
                return existing ? { ...col, order: existing.order } : { ...col };
            })
            .sort(sortColumns);
    };

    const onReorderTableColumn = (event: DataTableColumnReorderEvent) => {
        if (!tableColumns.value || event.dragIndex === 0 || event.dropIndex === 0) {
            return;
        }

        const adjustedDragIndex = event.dragIndex - 1;
        const adjustedDropIndex = event.dropIndex - 1;

        const visible = (tableColumns.value ?? []).filter((c) => c.order != null);
        const hiddenColumns = tableColumns.value.filter((c) => c.order == null);

        const getBlockIds = (columnId: string): string[] => {
            const ids = [columnId];

            const addChildrenRecursively = (parentId: string) => {
                const children = expandedColumns.value[parentId]?.children ?? [];
                children.forEach((child) => {
                    ids.push(child.id);
                    addChildrenRecursively(child.id);
                });
            };

            addChildrenRecursively(columnId);
            return ids;
        };

        const findBlockStartIndex = (columns: ResponseColumnHeader[], index: number) => {
            let current = columns[index];

            while (current?.expandsFromParentId) {
                const parentIndex = columns.findIndex((c) => c.id === current.expandsFromParentId);
                if (parentIndex === -1) break;
                current = columns[parentIndex];
                index = parentIndex;
            }

            return index;
        };

        const dragStartIndex = findBlockStartIndex(visible, adjustedDragIndex);
        const dragRoot = visible[dragStartIndex];
        if (!dragRoot) return;

        const draggedBlockIds = getBlockIds(dragRoot.id);
        const draggedBlock = visible.filter((col) => draggedBlockIds.includes(col.id));
        const remaining = visible.filter((col) => !draggedBlockIds.includes(col.id));

        const dropStartIndexInVisible = findBlockStartIndex(visible, adjustedDropIndex);
        const dropRoot = visible[dropStartIndexInVisible];
        if (!dropRoot) return;

        if (dropRoot.id === dragRoot.id) return;

        const dropIndexInRemaining = remaining.findIndex((col) => col.id === dropRoot.id);

        let insertIndex = dropIndexInRemaining;
        if (insertIndex === -1) {
            insertIndex = remaining.length;
        } else if (dragStartIndex < dropStartIndexInVisible) {
            insertIndex += 1;
        }

        const reorderedVisibleColumns = [...remaining];
        reorderedVisibleColumns.splice(insertIndex, 0, ...draggedBlock);
        reorderedVisibleColumns.forEach((col, index) => {
            col.order = index;
        });

        tableColumns.value = [...reorderedVisibleColumns, ...hiddenColumns];
    };

    const ensureProtocolColumnsFirst = () => {
        if (!tableColumns.value) return;
        const visible = tableColumns.value.filter((c) => c.order != null);
        const hidden = tableColumns.value.filter((c) => c.order == null);
        const protocolCols = visible.filter(
            (col) => col.type === 'ProtocolResults' || !!col.expandsFromParentId
        );
        const regularCols = visible.filter(
            (col) => col.type !== 'ProtocolResults' && !col.expandsFromParentId
        );
        if (!protocolCols.length) return;
        const finalOrder = [...protocolCols, ...regularCols];
        finalOrder.forEach((col, index) => {
            col.order = index;
        });
        tableColumns.value = [...finalOrder, ...hidden];
    };

    const setRowsIdList = (rowIds: string[]) => {
        rowsIdList.value = rowIds;
        if (showSummary.value) {
            expandedRows.value = {};
            expandedInSummaryMode.value = {};
            rowIds.forEach((id) => {
                expandedRows.value[id] = true;
            });
        } else if (allRowsExpansionActive.value) {
            expandedRows.value = {};
            rowIds.forEach((id) => {
                expandedRows.value[id] = true;
            });
        } else {
            expandedRows.value = {};
        }
    };

    const toggleRowExpansion = (rowId: string) => {
        if (expandedRows.value[rowId]) {
            delete expandedRows.value[rowId];
        } else {
            expandedRows.value[rowId] = true;
            openedRows.value[rowId] = true;
        }
    };

    const toggleAllRowsExpansion = () => {
        if (allRowsExpansionActive.value) {
            allRowsExpansionActive.value = false;
            expandedRows.value = {};
            return;
        }

        showSummary.value = false;
        expandedInSummaryMode.value = {};
        allRowsExpansionActive.value = true;
        expandedRows.value = {};

        rowsIdList.value.forEach((item) => {
            expandedRows.value[item] = true;
        });
    };

    const toggleRowSelection = (rowId: string) => {
        if (selectedRows.value[rowId]) {
            delete selectedRows.value[rowId];
        } else {
            selectedRows.value[rowId] = true;
        }
    };

    const toggleSelectionMode = () => {
        selectionMode.value = !selectionMode.value;
        if (!selectionMode.value) {
            selectedRows.value = {};
        }
    };

    const expandableParents = computed(() =>
        Object.entries(expandedColumns.value)
            .filter(([, v]) => (v.children?.length ?? 0) > 0)
            .map(([id]) => id)
    );

    const allColumnsExpanded = computed(
        () =>
            expandableParents.value.length > 0 &&
            expandableParents.value.every((id) => expandedColumns.value[id]?.open === true)
    );

    const toggleAllColumnsExpansion = () => {
        const shouldExpand = !allColumnsExpanded.value;

        expandableParents.value.forEach((id) => {
            expandedColumns.value[id].open = shouldExpand;
        });

        if (!shouldExpand) {
            Object.values(expandedColumns.value).forEach((node) => {
                node.open = false;
            });
        }
    };
    const toggleRowSummaryExpansion = (rowId: string) => {
        if (expandedInSummaryMode.value[rowId]) {
            delete expandedInSummaryMode.value[rowId];
        } else {
            expandedInSummaryMode.value[rowId] = true;
        }
    };

    const toggleShowSummary = () => {
        if (showSummary.value) {
            showSummary.value = false;
            expandedRows.value = {};
            expandedInSummaryMode.value = {};
            return;
        }

        if (allRowsExpansionActive.value) return;
        showSummary.value = true;
        expandedRows.value = {};
        expandedInSummaryMode.value = {};

        rowsIdList.value.forEach((item) => {
            expandedRows.value[item] = true;
        });
    };

    const hideColumn = (columnId: string) => {
        const col = tableColumns.value?.find((c) => c.id === columnId);
        if (col) col.order = null;
    };

    const clearSelection = () => {
        selectedRows.value = {};
    };

    const setSelectionMode = (enabled: boolean) => {
        selectionMode.value = enabled;
        if (!enabled) selectedRows.value = {};
    };
    return {
        expandedRows,
        openedRows,
        toggleRowExpansion,
        allRowsExpanded,
        allRowsExpansionActive,
        toggleAllRowsExpansion,
        selectedRows,
        toggleRowSelection,
        selectionMode,
        toggleSelectionMode,
        setRowsIdList,
        currentSortFieldId,
        currentSortOrder,
        showSummary,
        toggleShowSummary,
        expandedInSummaryMode,
        toggleRowSummaryExpansion,
        tableColumns,
        setTableColumns,
        reconcileTableColumns,
        onReorderTableColumn,
        showCoachResultAsPoints,
        expandedColumns,
        toggleColumnExpansion,
        setExpandableColumns,
        showHeatMap,
        showOnlyMyEvents,
        allColumnsExpanded,
        toggleAllColumnsExpansion,
        clearSelection,
        setSelectionMode,
        visibleColumns,
        hideColumn,
        ensureProtocolColumnsFirst,
    };
};

export enum InteractionTableStoreName {
    InteractionsTable = 'interactions-table',
    ProtocolTable = 'protocol-table',
    OrganisationalTable = 'organisational-table',
}

export const useInteractionTableStoreByName = (name: InteractionTableStoreName) => {
    return defineStore(name, interactionsTableStore)();
};
